import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { defaultPageNo } from "utils/constants";

export interface ContentArchiveResult {
  successCount: number;
  successIds: string[];
  skippedIds: string[];
  failedIds: string[];
}

interface ContentArchiveOptions {
  /** Used in every user-facing string, e.g. { singular: "pack", plural: "packs" }. */
  label: { singular: string; plural: string };
  /** True when the record is live in-game and therefore cannot be archived. */
  isLive?: (row: any) => boolean;
  archive: (ids: string[]) => Promise<any>;
  unarchive: (ids: string[]) => Promise<any>;
  /** Refresh the list once the action has finished. */
  onCompleted: () => void | Promise<void>;
  /** Reset pagination when the view switches between live and archived. */
  onViewChange?: () => void;
}

/**
 * Drives the archive/restore experience for one list screen: which view is showing, what is
 * selected, and the confirm -> act -> report cycle.
 *
 * The view lives in an `archived` query param rather than component state, so the archived list
 * is a real address - the browser back button, a refresh and "open in new tab" all behave.
 */
const useContentArchive = ({
  label,
  isLive,
  archive,
  unarchive,
  onCompleted,
  onViewChange,
}: ContentArchiveOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const archived = searchParams.get("archived") === "true";

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<ContentArchiveResult | null>(null);
  const [busy, setBusy] = useState(false);

  // A selection made in one view means nothing in the other.
  useEffect(() => {
    setSelectedIds([]);
  }, [archived]);

  /**
   * Switches between the live list and the archive in exactly ONE navigation.
   *
   * react-router's `setSearchParams` is not a state updater: its functional form is handed the
   * search params captured in the calling render, not the latest ones, and each call navigates to
   * a freshly built query string. Two calls in the same handler therefore both build off the same
   * stale snapshot and the last one silently discards the first one's change. `onViewChange` runs
   * first precisely so that its own param write is the one superseded, while its local pagination
   * reset still applies. Replacing rather than pushing then collapses both navigations into the
   * single history entry `onViewChange` already pushed, so one press of the browser's back button
   * leaves the archive.
   */
  const setView = (nextArchived: boolean) => {
    onViewChange?.();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextArchived) next.set("archived", "true");
        else next.delete("archived");
        next.set("pageNo", String(defaultPageNo));
        return next;
      },
      { replace: true },
    );
  };

  const showArchived = () => setView(true);
  const showLive = () => setView(false);

  /** Non-null disables the row's checkbox and explains why. */
  const rowSelectDisabledReason = useMemo(
    () => (row: any) =>
      !archived && isLive?.(row)
        ? `An active ${label.singular} cannot be archived. Deactivate it first.`
        : null,
    [archived, isLive, label.singular],
  );

  const requestAction = (ids?: string[]) => {
    const target = ids?.length ? ids : selectedIds;
    if (!target.length) return;
    setSelectedIds(target);
    setConfirmOpen(true);
  };

  const confirmAction = async () => {
    setBusy(true);
    try {
      const response = archived
        ? await unarchive(selectedIds)
        : await archive(selectedIds);

      // The backend reports per-id outcomes; a partial result is a success, not an error.
      const data: ContentArchiveResult = response?.data ?? response;
      setConfirmOpen(false);
      setResult({
        successCount: data?.successCount ?? 0,
        successIds: data?.successIds ?? [],
        skippedIds: data?.skippedIds ?? [],
        failedIds: data?.failedIds ?? [],
      });
      setSelectedIds([]);
      await onCompleted();
    } finally {
      setBusy(false);
    }
  };

  return {
    label,
    archived,
    selectedIds,
    setSelectedIds,
    clearSelection: () => setSelectedIds([]),
    showArchived,
    showLive,
    rowSelectDisabledReason,
    requestAction,
    confirmAction,
    confirmOpen,
    closeConfirm: () => setConfirmOpen(false),
    result,
    closeResult: () => setResult(null),
    busy,
  };
};

export type ContentArchive = ReturnType<typeof useContentArchive>;

export default useContentArchive;
